import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartNoAxesCombined, ChevronDown, ChevronRight, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import { fotoUrl } from "@/lib/user";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const dinero = (n) =>
  `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const horasTxt = (n) => {
  const h = Math.floor(Number(n || 0));
  const m = Math.round((Number(n || 0) - h) * 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};
const roundPct = (horas, presupuesto) =>
  presupuesto > 0 ? Math.round((Number(horas || 0) / presupuesto) * 1000) / 10 : null;

function KpiCard({ label, value, accent = "violet" }) {
  const estilos = {
    violet: "border-violet-100 bg-violet-50/50",
    emerald: "border-emerald-100 bg-emerald-50/50",
    amber: "border-amber-100 bg-amber-50/40",
    sky: "border-sky-100 bg-sky-50/50",
    rose: "border-rose-100 bg-rose-50/40",
  };
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${estilos[accent] || estilos.violet}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
    </div>
  );
}

function AvatarTrabajador({ persona, size = "lg" }) {
  const url = fotoUrl(persona);
  const cls = size === "lg" ? "h-20 w-20 text-2xl" : "h-11 w-11 text-sm";
  if (url) {
    return <img src={url} alt="" className={`${cls} rounded-full object-cover ring-2 ring-white shadow`} />;
  }
  const ini = (persona?.usuario || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  return (
    <div className={`${cls} flex items-center justify-center rounded-full bg-violet-100 font-semibold text-violet-800 ring-2 ring-white shadow`}>
      {ini || <UserRound className="h-5 w-5" />}
    </div>
  );
}

function fmtPctPresupuesto(pct, presupuesto) {
  if (pct == null) return presupuesto ? "0%" : "Sin presupuesto";
  return `${pct}%`;
}

function ListaJerarquia({ jerarquia, modoPct, activoId, onHover }) {
  const [abiertos, setAbiertos] = useState(() => new Set(jerarquia.map((j) => j.proyecto_id ?? "general")));
  useEffect(() => {
    setAbiertos(new Set(jerarquia.map((j) => j.proyecto_id ?? "general")));
  }, [jerarquia]);

  if (!jerarquia.length) {
    return <p className="py-10 text-center text-sm text-zinc-500">Sin horas imputadas en el periodo.</p>;
  }

  return (
    <ul className="space-y-1">
      {jerarquia.map((p) => {
        const key = p.proyecto_id ?? "general";
        const abierto = abiertos.has(key);
        const horasProc = p.horas_procesadas ?? (p.horas_totales + (p.horas_descanso || 0));
        return (
          <li key={key}>
            <button
              type="button"
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition ${
                activoId === `p-${key}` ? "bg-zinc-100" : "hover:bg-zinc-50"
              }`}
              onMouseEnter={() => onHover?.(`p-${key}`, { ...p, proyecto_nombre: p.proyecto_nombre })}
              onClick={() =>
                setAbiertos((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                })
              }
            >
              {abierto ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
              <span className="h-3 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: p.proyecto_color }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-zinc-900">
                  {p.proyecto_codigo ? `${p.proyecto_codigo} · ` : ""}{p.proyecto_nombre}
                </span>
                <span className="block text-[11px] text-zinc-500">
                  {horasTxt(p.horas_totales)} trabajo
                  {p.horas_descanso > 0 ? ` · ${horasTxt(p.horas_descanso)} descanso` : ""}
                  {p.presupuesto_horas ? ` · presup. ${horasTxt(p.presupuesto_horas)}` : ""}
                </span>
              </span>
              <span className="shrink-0 text-right text-sm font-medium text-zinc-700">
                {modoPct
                  ? (
                    <span title="Consumo del presupuesto de horas del proyecto">
                      {fmtPctPresupuesto(p.pct, p.presupuesto_horas)}
                    </span>
                  )
                  : horasTxt(horasProc)}
              </span>
            </button>
            {abierto && (
              <ul className="ml-6 space-y-0.5 border-l border-zinc-100 pl-3">
                {(p.tareas || []).map((t) => {
                  const horasT = (t.horas_totales || 0) + (t.horas_descanso || 0);
                  return (
                    <li
                      key={`${key}-${t.tarea_id || "sin"}`}
                      className="rounded-md px-2 py-1.5 text-sm"
                      onMouseEnter={() =>
                        onHover?.(`t-${key}-${t.tarea_id}`, { ...t, proyecto_nombre: p.proyecto_nombre, presupuesto_horas: p.presupuesto_horas })
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-1 shrink-0 rounded-full" style={{ backgroundColor: t.color || p.proyecto_color }} />
                        <span className="min-w-0 flex-1 truncate text-zinc-700">{t.tarea_nombre}</span>
                        <span className="text-xs text-zinc-500">
                          {modoPct ? fmtPctPresupuesto(t.pct, p.presupuesto_horas) : horasTxt(t.horas_totales || 0)}
                        </span>
                      </div>
                      {t.horas_descanso > 0 && (
                        <div className="ml-3 mt-0.5 flex items-center gap-2 text-[11px] text-zinc-500">
                          <span className="h-2 w-1 shrink-0 rounded-full bg-zinc-400" />
                          <span className="flex-1">Descanso en esta fase</span>
                          <span>{horasTxt(t.horas_descanso)}</span>
                        </div>
                      )}
                      {!modoPct && t.horas_descanso > 0 && (
                        <div className="ml-3 text-[10px] text-zinc-400">Total fase: {horasTxt(horasT)}</div>
                      )}
                    </li>
                  );
                })}
                {p.horas_descanso > 0 && (
                  <li className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-zinc-600">
                    <span className="h-3 w-1 shrink-0 rounded-full bg-zinc-400" />
                    <span className="flex-1">Descanso acumulado del proyecto</span>
                    <span>{horasTxt(p.horas_descanso)}</span>
                  </li>
                )}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function UtilizacionChart({ data, meta, modoPct }) {
  if (!meta?.length) {
    return <p className="py-12 text-center text-sm text-zinc-500">Sin datos para graficar.</p>;
  }
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit={modoPct ? "%" : "h"} domain={modoPct ? [0, 100] : ["auto", "auto"]} />
          <Tooltip
            formatter={(v, name) => {
              const m = meta.find((x) => x.clave === name || `${x.codigo} · ${x.nombre}` === name);
              const label = m ? `${m.codigo ? `${m.codigo} · ` : ""}${m.nombre}` : name;
              return [modoPct ? `${v}%` : `${v} h`, label];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {meta.map((m) => (
            <Area
              key={m.clave}
              type="monotone"
              dataKey={modoPct ? `${m.clave}_pct` : m.clave}
              name={`${m.codigo ? `${m.codigo} · ` : ""}${m.nombre}`}
              stackId="1"
              stroke={m.color}
              fill={m.color}
              fillOpacity={0.55}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ProyectosRentabilidad({
  clientes = [],
  proyectos = [],
  proyectoId = null,
  empleados = [],
}) {
  const [ambito, setAmbito] = useState("general");
  const [desde, setDesde] = useState(() => `${hoyISO().slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoyISO());
  const [clienteId, setClienteId] = useState("todos");
  const [proyId, setProyId] = useState(proyectoId ? String(proyectoId) : "todos");
  const [usuarioId, setUsuarioId] = useState("todos");
  const [data, setData] = useState(null);
  const [modoPct, setModoPct] = useState(false);
  const [hover, setHover] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: respuesta } = await api.get("/proyectos/rentabilidad", {
        params: {
          desde,
          hasta,
          proyecto_id: proyId === "todos" ? undefined : proyId,
          cliente_id: clienteId === "todos" ? undefined : clienteId,
          usuario_id: ambito === "trabajador" && usuarioId !== "todos" ? usuarioId : undefined,
        },
      });
      setData(respuesta);
      setHover(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, proyId, clienteId, usuarioId, ambito]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (ambito === "trabajador" && usuarioId === "todos" && data?.por_usuario?.length) {
      setUsuarioId(String(data.por_usuario[0].usuario_id));
    }
  }, [ambito, data, usuarioId]);

  const kpis = data?.kpis || {};
  const jerarquia = useMemo(() => data?.jerarquia || [], [data]);
  const meta = data?.proyectos_meta || [];
  const trabajadores = data?.por_usuario || [];
  const trabajadorSel =
    data?.trabajador ||
    trabajadores.find((t) => String(t.usuario_id) === usuarioId) ||
    null;

  const donutData = useMemo(() => {
    const hojas = [];
    jerarquia.forEach((p) => {
      (p.tareas || []).forEach((t) => {
        if (t.horas_totales > 0) {
          hojas.push({
            id: `t-${p.proyecto_id}-${t.tarea_id ?? "sin"}`,
            name: t.tarea_nombre,
            value: modoPct ? (t.pct || 0) : t.horas_totales,
            horas: t.horas_totales,
            pct: t.pct,
            color: t.color || p.proyecto_color,
            proyecto: p.proyecto_nombre,
            presupuesto: p.presupuesto_horas,
          });
        }
        if (t.horas_descanso > 0) {
          hojas.push({
            id: `d-${p.proyecto_id}-${t.tarea_id ?? "sin"}`,
            name: `Descanso · ${t.tarea_nombre}`,
            value: modoPct
              ? (p.presupuesto_horas ? roundPct(t.horas_descanso, p.presupuesto_horas) : 0)
              : t.horas_descanso,
            horas: t.horas_descanso,
            pct: p.presupuesto_horas ? roundPct(t.horas_descanso, p.presupuesto_horas) : null,
            color: "#94a3b8",
            proyecto: p.proyecto_nombre,
            presupuesto: p.presupuesto_horas,
          });
        }
      });
    });
    return hojas;
  }, [jerarquia, modoPct]);

  const totalHoras = kpis.horas_con_descanso ?? ((kpis.horas_totales || 0) + (kpis.horas_descanso || 0));

  return (
    <div className="space-y-5" data-testid="proyectos-rentabilidad-visual">
      <Tabs value={ambito} onValueChange={setAmbito}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="trabajador">Por trabajador</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/80 p-4">
        <div>
          <Label>Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div>
          <Label>Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        {ambito === "general" && (
          <>
            <div>
              <Label>Cliente</Label>
              <Select value={clienteId} onValueChange={setClienteId}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {clientes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proyecto</Label>
              <Select value={proyId} onValueChange={setProyId}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {proyectos.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.codigo || p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {ambito === "trabajador" && (
          <div>
            <Label>Trabajador</Label>
            <Select value={usuarioId} onValueChange={setUsuarioId}>
              <SelectTrigger className="w-56"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                {(trabajadores.length
                  ? trabajadores
                  : empleados.map((e) => ({
                      usuario_id: e.id,
                      usuario: `${e.nombre} ${e.apellidos || ""}`.trim(),
                    }))
                ).map((t) => (
                  <SelectItem key={t.usuario_id} value={String(t.usuario_id)}>
                    {t.usuario}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={cargar} disabled={cargando} className="gap-2">
          <ChartNoAxesCombined className="h-4 w-4" /> Consultar
        </Button>
        <div className="ml-auto flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-2">
          <span className={`text-xs ${!modoPct ? "font-semibold text-zinc-900" : "text-zinc-500"}`}>En horas</span>
          <Switch checked={modoPct} onCheckedChange={setModoPct} />
          <span className={`text-xs ${modoPct ? "font-semibold text-zinc-900" : "text-zinc-500"}`}>% presupuesto</span>
        </div>
      </div>

      {data && ambito === "trabajador" && (
        <>
          <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
            <aside className="space-y-2 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
              <p className="px-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Equipo</p>
              <div className="max-h-[420px] space-y-1 overflow-y-auto">
                {trabajadores.map((t) => {
                  const sel = String(t.usuario_id) === usuarioId;
                  return (
                    <button
                      key={t.usuario_id}
                      type="button"
                      onClick={() => setUsuarioId(String(t.usuario_id))}
                      className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition ${
                        sel ? "bg-violet-50 ring-1 ring-violet-200" : "hover:bg-zinc-50"
                      }`}
                    >
                      <AvatarTrabajador persona={t} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-zinc-900">{t.usuario}</p>
                        <p className="truncate text-[11px] text-zinc-500">
                          {t.departamento || "—"} · {horasTxt(t.horas_totales)}
                        </p>
                      </div>
                    </button>
                  );
                })}
                {!trabajadores.length && (
                  <p className="px-2 py-8 text-center text-sm text-zinc-500">
                    Nadie ha imputado horas a proyectos en este periodo.
                  </p>
                )}
              </div>
            </aside>

            <div className="space-y-4">
              {trabajadorSel && (
                <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-zinc-200 bg-gradient-to-r from-sky-50 to-violet-50 p-5 shadow-sm">
                  <AvatarTrabajador persona={trabajadorSel} />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-xl font-semibold text-zinc-900">{trabajadorSel.usuario}</p>
                    <p className="text-sm text-zinc-600">
                      {trabajadorSel.departamento || "Sin departamento"} · {horasTxt(trabajadorSel.horas_totales || kpis.horas_totales)} en proyectos
                    </p>
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <KpiCard label="Ingresos totales" value={dinero(kpis.ingreso_estimado)} accent="emerald" />
                <KpiCard label="Costes internos totales" value={dinero(kpis.coste_interno)} accent="amber" />
                <KpiCard label="Ingresos medios por hora" value={dinero(kpis.ingreso_medio_hora)} accent="sky" />
              </div>

              <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
                <div className="border-b border-zinc-100 px-5 py-4">
                  <h3 className="font-display text-lg font-medium text-zinc-900">Utilización a lo largo del tiempo</h3>
                  <p className="text-xs text-zinc-500">Cómo reparte este trabajador su tiempo entre proyectos</p>
                </div>
                <div className="p-4">
                  <UtilizacionChart data={data.utilizacion_diaria} meta={meta} modoPct={modoPct} />
                </div>
              </section>

              <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
                <div className="border-b border-zinc-100 px-5 py-4">
                  <h3 className="font-display text-lg font-medium text-zinc-900">Proyectos del trabajador</h3>
                </div>
                <ul className="divide-y divide-zinc-100">
                  {(trabajadorSel?.proyectos || []).map((p) => (
                    <li key={p.proyecto_id} className="flex items-center gap-3 px-5 py-3">
                      <span className="h-8 w-1.5 rounded-full" style={{ backgroundColor: p.proyecto_color }} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-zinc-900">
                          {p.proyecto_codigo ? `${p.proyecto_codigo} · ` : ""}{p.proyecto_nombre}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {modoPct ? `${p.pct}%` : horasTxt(p.horas_totales)} · ingreso {dinero(p.ingreso_estimado)} · coste {dinero(p.coste_interno)}
                        </p>
                      </div>
                      <div className="w-24">
                        <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.min(100, p.pct || 0)}%`, backgroundColor: p.proyecto_color }}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                  {!trabajadorSel?.proyectos?.length && (
                    <li className="px-5 py-10 text-center text-sm text-zinc-500">Sin proyectos imputados.</li>
                  )}
                </ul>
              </section>
            </div>
          </div>
        </>
      )}

      {data && ambito === "general" && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard label="Ingresos totales" value={dinero(kpis.ingreso_estimado)} accent="emerald" />
            <KpiCard label="Costes internos totales" value={dinero(kpis.coste_interno)} accent="amber" />
            <KpiCard label="Ingresos medios por hora" value={dinero(kpis.ingreso_medio_hora)} accent="sky" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard label="Horas totales" value={horasTxt(kpis.horas_totales)} />
            <KpiCard label="Gastos aprobados" value={dinero(kpis.gastos_aprobados)} accent="rose" />
            <KpiCard label="Margen estimado" value={dinero(kpis.margen)} accent="emerald" />
          </div>

          <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-5 py-4">
              <h3 className="font-display text-lg font-medium text-zinc-900">Resumen</h3>
              <p className="text-xs text-zinc-500">
                Por proyecto y fase. El % es el consumo del presupuesto de horas del proyecto. El descanso se imputa a la fase en curso.
              </p>
            </div>
            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <div className="relative mx-auto h-72 w-full max-w-md">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={1}
                      onMouseEnter={(_, i) => {
                        const d = donutData[i];
                        if (d) setHover({ id: d.id, name: d.name, proyecto: d.proyecto, horas: d.horas, pct: d.pct, color: d.color });
                      }}
                    >
                      {donutData.map((d) => (
                        <Cell key={d.id} fill={d.color} opacity={!hover || hover.id === d.id ? 1 : 0.35} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, _n, item) => [
                        modoPct ? `${item?.payload?.pct ?? v}%` : horasTxt(item?.payload?.horas ?? v),
                        item?.payload?.name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-xs uppercase tracking-wide text-zinc-400">Total</p>
                  <p className="font-display text-2xl font-semibold text-zinc-900">{horasTxt(totalHoras)}</p>
                  {hover && (
                    <p className="mt-1 max-w-[140px] truncate text-center text-xs text-zinc-500">
                      {hover.name}: {modoPct ? `${hover.pct}%` : horasTxt(hover.horas)}
                    </p>
                  )}
                </div>
              </div>
              <div>
                {hover && (
                  <div className="mb-3 flex items-center gap-3 rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2">
                    <span className="h-8 w-1.5 rounded-full" style={{ backgroundColor: hover.color }} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-zinc-900">{hover.name}</p>
                      <p className="truncate text-xs text-zinc-500">{hover.proyecto}</p>
                    </div>
                    <div className="text-right text-sm font-medium text-zinc-800">
                      <p>
                        {hover.pct != null
                          ? `${hover.pct}% del presupuesto`
                          : hover.presupuesto
                            ? "Sin consumo"
                            : "Sin presupuesto"}
                        {" · "}
                        {horasTxt(hover.horas)}
                      </p>
                    </div>
                  </div>
                )}
                <ListaJerarquia
                  jerarquia={jerarquia}
                  modoPct={modoPct}
                  activoId={hover?.id}
                  onHover={(id, item) =>
                    setHover({
                      id,
                      name: item.tarea_nombre || item.proyecto_nombre,
                      proyecto: item.proyecto_nombre,
                      horas: item.horas_totales,
                      pct: item.pct,
                      color: item.proyecto_color || item.color,
                    })
                  }
                />
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-5 py-4">
              <h3 className="font-display text-lg font-medium text-zinc-900">Utilización a lo largo del tiempo</h3>
            </div>
            <div className="p-4">
              <UtilizacionChart data={data.utilizacion_diaria} meta={meta} modoPct={modoPct} />
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 px-5 py-4">
              <h3 className="font-display text-lg font-medium text-zinc-900">Por trabajador</h3>
              <p className="text-xs text-zinc-500">Ranking de horas e ingresos en el periodo</p>
            </div>
            <ul className="divide-y divide-zinc-100">
              {trabajadores.map((t) => (
                <li key={t.usuario_id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-zinc-50"
                    onClick={() => {
                      setAmbito("trabajador");
                      setUsuarioId(String(t.usuario_id));
                    }}
                  >
                    <AvatarTrabajador persona={t} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-zinc-900">{t.usuario}</p>
                      <p className="text-xs text-zinc-500">{t.departamento || "—"}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-medium text-zinc-900">{horasTxt(t.horas_totales)}</p>
                      <p className="text-xs text-emerald-700">{dinero(t.ingreso_estimado)}</p>
                    </div>
                  </button>
                </li>
              ))}
              {!trabajadores.length && (
                <li className="px-5 py-10 text-center text-sm text-zinc-500">Sin datos de trabajadores.</li>
              )}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
